import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Check, Loader2, Users, Clock } from "lucide-react";

export default function Referral() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/referral").then(r => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const copy = async () => {
    try { await navigator.clipboard.writeText(data.url); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Referral link copied"); }
    catch { toast.error("Copy failed — select the link manually."); }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const r = await api.post("/referral/redeem", { code: code.trim() });
      toast.success(`Redeemed — ${r.data.reward_days} days of Pro added 🎉`);
      setCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't redeem that code");
    } finally { setRedeeming(false); }
  };

  if (!data) return <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>;

  return (
    <div className="space-y-6 fade-up max-w-3xl">
      <header>
        <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// REFER & EARN</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight" data-testid="referral-heading">Give a month, get a month</h1>
        <p className="text-[var(--text-2)] mt-2 max-w-2xl">Share your link with a league mate. When they redeem it, you <span className="text-white">both get 30 days of Pro free</span> — no card required.</p>
      </header>

      <div className="grid grid-cols-2 gap-3">
        <div className="card-bordered p-5" data-testid="referral-count">
          <div className="text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> FRIENDS REFERRED</div>
          <div className="font-mono text-3xl font-bold mt-1">{data.referred_count}</div>
        </div>
        <div className="card-bordered p-5" data-testid="referral-days">
          <div className="text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> PRO DAYS EARNED</div>
          <div className="font-mono text-3xl font-bold mt-1 text-[var(--accent)]">{data.days_earned}</div>
        </div>
      </div>

      <div className="card-bordered p-5">
        <div className="text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono mb-3">YOUR REFERRAL LINK</div>
        <div className="flex gap-2">
          <input readOnly value={data.url} className="input-dark flex-1 font-mono text-sm" data-testid="referral-link" onFocus={(e) => e.target.select()} />
          <button onClick={copy} className="btn-primary text-sm inline-flex items-center gap-2 shrink-0" data-testid="referral-copy-btn">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy
          </button>
        </div>
        <div className="text-xs text-[var(--text-3)] mt-2">Code: <span className="font-mono text-[var(--text-2)]">{data.code}</span></div>
      </div>

      {!data.redeemed && (
        <div className="card-bordered p-5" data-testid="redeem-box">
          <div className="text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono mb-3 flex items-center gap-1.5"><Gift className="w-3.5 h-3.5 text-[var(--accent)]" /> HAVE A CODE? REDEEM FOR 30 FREE DAYS</div>
          <div className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ENTER CODE" className="input-dark flex-1 font-mono text-sm uppercase" data-testid="redeem-input" />
            <button onClick={redeem} disabled={redeeming || !code.trim()} className="btn-secondary text-sm inline-flex items-center gap-2 shrink-0 disabled:opacity-50" data-testid="redeem-btn">
              {redeeming ? <Loader2 className="w-4 h-4 spin" /> : <Gift className="w-4 h-4" />} Redeem
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
